<template>
  <div>
    <div class="mt-2" v-if="isSearchResultsEmpty">
      <vs-button
        full-width
        :label="`Create client: ${search}...`"
        type="secondary"
        @click="handleOpenCreateClientForm"
      />
    </div>

    <v-progress-circular
      v-if="isLoading"
      class="ma-auto"
      :size="64"
      :width="6"
      indeterminate
      color="primary"
    />

    <div v-else-if="!isSearchResultsEmpty">
      <connection-request-confirmation-dialog
        v-if="showConfirmationDialog && selectedHub"
        :hub-name="selectedHub.name"
        :org-name="selectedHub.ownerName"
        :auto-connect="!selectedHub.canManageConnections"
        @close="showConfirmationDialog = false"
        @confirm="handleSendConnection"
      />

      <v-list class="client-hubs-list">
        <template v-for="(client, i) in clients">
          <v-list-item v-if="client" :key="i">
            <v-list-item-content>
              <div class="hub-name">
                <vs-text bold>{{ client.ownerName }}</vs-text>

                <img
                  v-if="client.canManageConnections"
                  class="logo"
                  src="../../assets/logo_black.png"
                />
              </div>
              <vs-text>{{ client.name }}</vs-text>
            </v-list-item-content>
            <v-list-item-action>
              <vs-button
                :class="{
                  'connect-button': true,
                  'connect-button-disabled': client.connectionStatus != null,
                }"
                :label="getStatusChipText(client.connectionStatus, client.canManageConnections)"
                :loading="creatingConnection && selectedHub?.id === client.id"
                :destructive="client.connectionStatus === ConnectionStatus.Rejected"
                type="secondary"
                small
                @click="handleSelectHub(client.id)"
              />
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
      <vs-pagination :value="currentPage" :length="totalPages" @input="updatePage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePagination } from './pagination'
import { computed, reactive, ref, toRef } from 'vue'
import { useBuildSearchClientsQuery } from '@/components/companies/query'
import { useSearchClientsQuery } from '@/api/connection-request'
import ConnectionRequestConfirmationDialog from '@/components/companies/ConnectionRequestConfirmationDialog.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import VsPagination from '@/components/vision/VsPagination.vue'
import { Client, ConnectionStatus } from '@/models/client'
import { useStore } from 'vuex-composition-helpers'

const props = defineProps<{
  creatingConnection: boolean
  search: string
}>()

const store = useStore()
const canCreateClient = computed<boolean>(() => store.getters['permissions/canCreateClient'])
const { currentPage, updatePage } = usePagination()
const query = reactive(useBuildSearchClientsQuery(currentPage, 20, toRef(props, 'search')))
const isSearchResultsEmpty = computed(() => {
  return (clients.value?.length ?? 0) === 0
})
const { clients, totalPages, isLoading } = useSearchClientsQuery(query)

const showConfirmationDialog = ref<boolean>()
const selectedHub = ref<Client>()

const emit = defineEmits<{
  (e: 'select', hubId: string)
  (e: 'open-create-client-form')
}>()

function handleSelectHub(selectedHubId: string) {
  if (!canCreateClient.value) {
    return
  }
  selectedHub.value = clients.value?.find((c) => c.id === selectedHubId)
  showConfirmationDialog.value = true
}

function handleSendConnection() {
  showConfirmationDialog.value = false
  if (!selectedHub.value) {
    return
  }

  emit('select', selectedHub.value.id)
}

function getStatusChipText(connectionStatus, canManageConnections) {
  if (connectionStatus === ConnectionStatus.Pending) {
    return 'Pending'
  }

  if (connectionStatus === ConnectionStatus.Rejected) {
    return 'Rejected'
  }

  if (connectionStatus === ConnectionStatus.Accepted) {
    return 'Connected'
  }

  if (canManageConnections) {
    return 'Invite'
  }

  return 'Connect'
}

const handleOpenCreateClientForm = () => {
  emit('open-create-client-form')
}

defineExpose({ updatePage })
</script>

<style scoped>
.client-hubs-list {
  margin: -8px -16px;
}

.hub-name {
  display: flex;
  align-items: center;
}

.logo {
  margin-left: 6px;
  width: 16px;
}

.connect-button {
  width: 100px;
  justify-content: center;
}

.connect-button-disabled {
  opacity: 38%;
  pointer-events: none;
}
</style>
